import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch } from 'react-icons/fi';
import { SHARED_LIST_TYPES, getTypeMeta } from '../lib/sharedListTypes';
import NewSharedListModal from '../components/NewSharedListModal';
import './SharedLists.css';

const SharedLists = ({ token, boardId = null, boardName = null, embedded = false }) => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const navigate = useNavigate();

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchLists = async () => {
    setLoading(true);
    try {
      const qs = boardId ? `?boardId=${boardId}` : '';
      const r = await fetch(`${apiUrl}/api/shared-lists${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setLists(d.sharedLists || []);
      }
    } catch (e) {
      console.error('Failed to fetch shared lists:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLists();
    // eslint-disable-next-line
  }, [boardId]);

  const filtered = useMemo(() => {
    let out = lists;
    if (typeFilter !== 'all') out = out.filter((l) => l.type === typeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(
        (l) =>
          (l.title || '').toLowerCase().includes(q) ||
          (l.description || '').toLowerCase().includes(q)
      );
    }
    return out;
  }, [lists, search, typeFilter]);

  const countByType = useMemo(() => {
    const map = {};
    lists.forEach((l) => {
      map[l.type] = (map[l.type] || 0) + 1;
    });
    return map;
  }, [lists]);

  return (
    <div className={`shared-lists-page ${embedded ? 'is-embedded' : ''}`} data-testid="shared-lists-page">
      <div className="shared-lists-header">
        <div>
          <h1 className="shared-lists-title" data-testid="shared-lists-title">
            {embedded ? 'Shared Lists' : 'Shared Lists'}
          </h1>
          {embedded && boardName && (
            <div className="shared-lists-subtitle">Scoped to board: <strong>{boardName}</strong></div>
          )}
          {!embedded && (
            <div className="shared-lists-subtitle">
              Notes, checklists, snag lists and shopping lists. Share externally with a password if needed.
            </div>
          )}
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowNewModal(true)}
          data-testid="new-shared-list-button"
        >
          <FiPlus size={18} />
          <span>New List</span>
        </button>
      </div>

      <div className="shared-lists-toolbar">
        <div className="shared-lists-search">
          <FiSearch size={16} />
          <input
            type="text"
            placeholder="Search lists..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="shared-lists-search"
          />
        </div>
        <div className="shared-lists-type-filters">
          <button
            className={`type-chip ${typeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setTypeFilter('all')}
            data-testid="type-filter-all"
          >
            All ({lists.length})
          </button>
          {SHARED_LIST_TYPES.map((t) => {
            const Icon = t.icon;
            const n = countByType[t.value] || 0;
            if (n === 0 && typeFilter !== t.value) return null;
            return (
              <button
                key={t.value}
                className={`type-chip ${typeFilter === t.value ? 'active' : ''}`}
                style={{ '--type-color': t.color }}
                onClick={() => setTypeFilter(t.value)}
                data-testid={`type-filter-${t.value}`}
              >
                <Icon size={14} />
                <span>{t.label}</span>
                <span className="type-chip-count">{n}</span>
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner"></div>
      ) : filtered.length === 0 ? (
        <div className="shared-lists-empty" data-testid="shared-lists-empty">
          <div className="shared-lists-empty-title">No shared lists yet</div>
          <div className="shared-lists-empty-text">
            Pick a template (snag list, shopping list, basic notes, etc.) and start collaborating.
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowNewModal(true)}
            data-testid="empty-new-shared-list-button"
          >
            <FiPlus size={16} /> Create your first list
          </button>
        </div>
      ) : (
        <div className="shared-lists-grid">
          {filtered.map((l) => {
            const meta = getTypeMeta(l.type);
            const Icon = meta.icon;
            return (
              <Link
                key={l.id}
                to={`/shared-lists/${l.id}`}
                className="shared-list-card"
                data-testid="shared-list-card"
                style={{ borderLeft: `4px solid ${meta.color}` }}
              >
                <div className="shared-list-card-head">
                  <div className="shared-list-card-icon" style={{ background: `${meta.color}1A`, color: meta.color }}>
                    <Icon size={18} />
                  </div>
                  <span className="shared-list-card-type" style={{ color: meta.color }}>
                    {meta.label}
                  </span>
                  {l.externalShareEnabled && (
                    <span className="shared-list-card-share" title="External share enabled">
                      Shared
                    </span>
                  )}
                </div>
                <div className="shared-list-card-title">{l.title}</div>
                {l.description && <div className="shared-list-card-desc">{l.description}</div>}
                <div className="shared-list-card-foot">
                  <span>{l.itemsCount || 0} items</span>
                  <span className="sep">•</span>
                  <span>{new Date(l.updatedAt || l.createdAt).toLocaleDateString()}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <NewSharedListModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreated={(sl) => {
          setShowNewModal(false);
          if (sl && sl.id) {
            navigate(`/shared-lists/${sl.id}`);
          } else {
            fetchLists();
          }
        }}
        token={token}
        boardId={boardId}
      />
    </div>
  );
};

export default SharedLists;
